import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import Button from 'antd/es/button';
import Form, { type FormInstance } from 'antd/es/form';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import React, { useEffect } from 'react';
import styled from 'styled-components';

const StyledTextArea = styled(Input.TextArea)`
  min-height: 120px;
`;

const MetadataFormActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const KeyValueContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

interface TaskEditModalsProps {
  nameModalVisible: boolean;
  descriptionModalVisible: boolean;
  metadataModalVisible: boolean;
  nameForm: FormInstance;
  descriptionForm: FormInstance;
  metadataForm: FormInstance;
  metadata: Record<string, string>;
  onNameModalCancel: () => void;
  onDescriptionModalCancel: () => void;
  onMetadataModalCancel: () => void;
  onTitleSubmit: (values: { title: string }) => void;
  onDescriptionSubmit: (values: { description: string }) => void;
  onMetadataSubmit: (values: Record<string, string>) => void;
  isSubmitting: boolean;
}

const TaskEditModals: React.FC<TaskEditModalsProps> = ({
  nameModalVisible,
  descriptionModalVisible,
  metadataModalVisible,
  nameForm,
  descriptionForm,
  metadataForm,
  metadata,
  onNameModalCancel,
  onDescriptionModalCancel,
  onMetadataModalCancel,
  onTitleSubmit,
  onDescriptionSubmit,
  onMetadataSubmit,
  isSubmitting,
}) => {
  // Initialize the form with metadata when the modal becomes visible
  useEffect(() => {
    if (metadataModalVisible) {
      const initialValues = {
        metadataItems: Object.entries(metadata).map(([key, value]) => ({
          metaKey: key,
          metaValue: value,
        })),
      };
      metadataForm.setFieldsValue(initialValues);
    }
  }, [metadataModalVisible, metadata, metadataForm]);

  const handleMetadataSubmit = (values: {
    metadataItems: Array<{ metaKey: string; metaValue: string }>;
  }) => {
    // Convert the array of items back to a record
    const metadataRecord: Record<string, string> = {};

    values.metadataItems?.forEach((item) => {
      if (item.metaKey && item.metaKey.trim()) {
        metadataRecord[item.metaKey.trim()] = item.metaValue || '';
      }
    });

    onMetadataSubmit(metadataRecord);
  };

  const handleMetadataModalCancel = () => {
    onMetadataModalCancel();
  };

  return (
    <>
      {/* Edit Name Modal */}
      <Modal
        title="Edit Task Name"
        open={nameModalVisible}
        onCancel={onNameModalCancel}
        footer={null}
        destroyOnHidden
      >
        <Form form={nameForm} layout="vertical" onFinish={onTitleSubmit}>
          <Form.Item
            name="title"
            label="Task Title"
            rules={[{ required: true, message: 'Please enter a task title' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button onClick={onNameModalCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Description Modal */}
      <Modal
        title="Edit Task Description"
        open={descriptionModalVisible}
        onCancel={onDescriptionModalCancel}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={descriptionForm}
          layout="vertical"
          onFinish={onDescriptionSubmit}
        >
          <Form.Item name="description" label="Description">
            <StyledTextArea rows={6} />
          </Form.Item>
          <Form.Item>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button onClick={onDescriptionModalCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Metadata Modal */}
      <Modal
        title="Edit Metadata"
        open={metadataModalVisible}
        onCancel={handleMetadataModalCancel}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Form
          form={metadataForm}
          layout="vertical"
          onFinish={handleMetadataSubmit}
        >
          <Form.List name="metadataItems">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <KeyValueContainer key={key}>
                    <Form.Item
                      {...restField}
                      name={[name, 'metaKey']}
                      style={{ flex: 1, marginBottom: 8 }}
                      rules={[{ required: true, message: 'Key is required' }]}
                    >
                      <Input placeholder="Key" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'metaValue']}
                      style={{ flex: 1, marginBottom: 8 }}
                    >
                      <Input placeholder="Value" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 8 }}>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        icon={<DeleteOutlined />}
                      />
                    </Form.Item>
                  </KeyValueContainer>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add({ metaKey: '', metaValue: '' })}
                    block
                  >
                    Add Field
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <MetadataFormActions>
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <Button onClick={handleMetadataModalCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </MetadataFormActions>
        </Form>
      </Modal>
    </>
  );
};

export default TaskEditModals;
